import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ColumnFilterData,
  DateColumnFilter,
  IManagedDataTable,
  managedTableSearchParams,
  MultiValueColumnFilter,
  sortField,
} from './interfaces';
import {
  DataGridProps,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { DataTable } from '../data-table';
import { DataTableFilters } from '../data-table/DataTable/interfaces';
import {
  buildCheckFilter,
  buildDateFilter,
  hasActiveFilters,
  isSameSortField,
  mapFilterDataToFilterFields,
  readFilterAndSortSettingsFromLocalStorage,
  storeFilterAndSortSettingsInLocalStorage,
} from '../../helpers/managedTableHelper';
import useFormState from '../../hooks/useFormState';
import { useSearchParams } from 'react-router';
import { DateFilterValue } from '../data-table/filters/DateFilter/interfaces';

export const ManagedDataTable = <T,>({
  columns,
  searchPlaceholder,
  initialSort,
  storageKey,
  sx,
  extraQueryParams,
  searchable,
  emptyPlaceholder,
  columnFilters,
  filter: baseFilter,
  getRowHeight,
  getRowClassName,
  query,
  onRowClick,
}: IManagedDataTable<T>): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSortModel: GridSortModel = useMemo(() => {
    try {
      const parsed = JSON.parse(searchParams.get('sort') || '[]');

      if (Array.isArray(parsed) && parsed.length) {
        return parsed;
      }
    } catch (_) {
      // do nothing.
    }

    return initialSort ? [initialSort] : [];
  }, [searchParams, initialSort]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    () => ({
      page: parseInt(searchParams.get('page') || '1') - 1,
      pageSize: parseInt(searchParams.get('pageSize') || '10'),
    })
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);
  const [search, setSearch] = useState<string>(() => {
    return searchParams.get('search') || '';
  });

  const handleRowClick: DataGridProps['onRowClick'] | undefined = onRowClick
    ? (data) => onRowClick && onRowClick(data.row)
    : undefined;

  const {
    formData: filterData,
    setFormFieldData: setFilterFieldData,
    setFormData: setFilterData,
  } = useFormState<ColumnFilterData>(
    (() => {
      return JSON.parse(searchParams.get('filters') || '{}');
    })()
  );

  const initialState = useMemo(
    () => ({
      ...(initialSortModel.length > 0 && {
        sorting: {
          sortModel: initialSortModel,
        },
      }),
    }),
    [initialSortModel]
  );

  const filter = useMemo(() => {
    const mappedFilters = mapFilterDataToFilterFields(filterData);

    return [...(mappedFilters || []), ...(baseFilter || [])];
  }, [filterData, baseFilter]);

  const sortField = (() => {
    const sortField = sortModel.at(0);

    if (sortField) {
      return sortField as sortField;
    }

    return undefined;
  })();

  const { data, isLoading } = query({
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    sortField,
    search: search || undefined,
    filter: filter,
    ...extraQueryParams,
  });

  const onSearch = (search: string) => {
    setSearch(search);
  };

  const getHeaderFilters = (): DataTableFilters => {
    const filters: DataTableFilters = {};

    columnFilters?.forEach((filter) => {
      if (filter.type === 'multivalue') {
        const multiValueFilter = filter as MultiValueColumnFilter;

        filters[multiValueFilter.column] = buildCheckFilter(
          multiValueFilter.filterName || multiValueFilter.column,
          multiValueFilter.label,
          multiValueFilter.options,
          setFilterFieldData,
          filterData[multiValueFilter.filterName || multiValueFilter.column] ||
            multiValueFilter.value
        );
      } else if (filter.type === 'date') {
        const dateFilter = filter as DateColumnFilter;

        filters[dateFilter.column] = buildDateFilter(
          dateFilter.filterName || dateFilter.column,
          dateFilter.label,
          setFilterFieldData,
          (filterData[dateFilter.filterName || dateFilter.column] ||
            dateFilter.value ||
            undefined) as DateFilterValue[]
        );
      }
    });

    return filters;
  };

  const clearFilter = (): void => {
    setFilterData({});
  };

  useEffect(() => {
    setSearchParams(() => {
      const newSearchParams: managedTableSearchParams = {};

      if (paginationModel.page > 0) {
        newSearchParams.page = paginationModel.page + 1 + '';
      }

      if (paginationModel.pageSize > 10) {
        newSearchParams.pageSize = paginationModel.pageSize + '';
      }

      if (search) {
        newSearchParams.search = search;
      }

      if (filterData && Object.keys(filterData).length) {
        newSearchParams.filters = JSON.stringify(filterData);
      }

      if (sortModel.length > 0) {
        const currentSort = sortModel[0];

        const isNotInitialSort =
          !initialSort ||
          !isSameSortField(currentSort as sortField, initialSort);

        if (isNotInitialSort) {
          newSearchParams.sort = JSON.stringify(sortModel);
        }
      }

      return newSearchParams;
    });
  }, [paginationModel, search, filterData, searchParams, sortModel]);

  useEffect(() => {
    if (searchParams.size === 0 && storageKey) {
      const storedFilterSettings =
        readFilterAndSortSettingsFromLocalStorage(storageKey);

      if (Object.keys(storedFilterSettings || {}).length) {
        setFilterData(storedFilterSettings?.filterData || {});

        if (storedFilterSettings?.sort) {
          setSortModel([storedFilterSettings.sort]);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!storageKey) {
      return;
    }

    storeFilterAndSortSettingsInLocalStorage(
      storageKey,
      searchParams.has('sort')
        ? JSON.parse(searchParams.get('sort') || '[]')[0]
        : null,
      searchParams.has('filters')
        ? JSON.parse(searchParams.get('filters') || '{}')
        : null
    );
  }, [searchParams]);

  if (emptyPlaceholder && data?.meta.total === 0) {
    return <Fragment>{emptyPlaceholder}</Fragment>;
  }

  return (
    <Fragment>
      <DataTable
        rows={data?.data}
        columns={columns}
        rowCount={data?.meta.total}
        isLoading={isLoading}
        paginationModel={paginationModel}
        searchable={searchable}
        sortModel={sortModel}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        onRowClick={handleRowClick}
        initialState={initialState}
        onSearch={onSearch}
        searchPlaceholder={searchPlaceholder}
        storageKey={storageKey} // move the responsibility for saving the view columns to this component.
        getRowClassName={getRowClassName}
        getRowHeight={getRowHeight}
        getHeaderFilters={getHeaderFilters}
        onClearFilters={clearFilter}
        hasActiveFilters={hasActiveFilters(filterData)}
        searchQuery={search}
        sx={sx}
      />
    </Fragment>
  );
};
